// lifetime-centiles-v0 reader (ba-life's BrainChart export, #2 comment 5971918792): a chart-only run, never sent to `vp.sim`.
// `curves.json` carries six series, GMV, WMV and ventricular volume in mL for each sex, each the 2.5th, 50th and 97.5th centiles
// at 1010 ages. The native age is post-conception days (BrainChart's own axis, Bethlehem 2022 supplement S9.2); the app shows it
// in years as days ÷ 365.25 and names the unit, and never converts it to postmenstrual age or age since birth: the header's
// mapping fields are null by design.

import type { SimFiles } from './files.ts';

export type CentileMetric = 'GMV' | 'WMV' | 'Ventricles';
export type CentileSex = 'Female' | 'Male';
export type CentileKey = '025' | '500' | '975';
export const METRICS: CentileMetric[] = ['GMV', 'WMV', 'Ventricles'];
export const SEXES: CentileSex[] = ['Female', 'Male'];
export const CENTILE_KEYS: CentileKey[] = ['025', '500', '975'];
export const METRIC_NAMES: Record<CentileMetric, string> = { GMV: 'Grey-matter volume', WMV: 'White-matter volume', Ventricles: 'Ventricular volume' };
export const DAYS_PER_YEAR = 365.25;
/** Post-conception days as years, the display axis (the header's `units.year_display`). */
export const ageYears = (days: number): number => days / DAYS_PER_YEAR;

export interface CentileSeries {
  metric: CentileMetric;
  sex: CentileSex;
  age_post_conception_days: number[];
  centiles_ml: Record<CentileKey, number[]>;
}

export interface CentilesHeader {
  format: 'lifetime-centiles-v0';
  id: string;
  domain?: string;
  kind?: string;
  label?: string;
  model?: { name?: string; doi?: string; repository_commit?: string; objects?: string[] };
  units?: { volume?: string; age?: string; year_display?: string };
  age?: { quantity?: string; units?: string; start?: number; end?: number; pma_mapping?: unknown; chronological_birth_offset_days?: unknown };
  time?: { base?: string; values_in?: string; interpolation?: string; extrapolation?: string };
  series?: { path?: string; count?: number; probabilities?: number[]; sha256?: string };
  methods?: string;
  pipeline?: { path?: string; commit?: string; packaging_path?: string };
  validation?: { checks?: Record<string, { rows?: number; cdf_roundtrip_max_abs?: number[]; all_finite_positive_ordered?: boolean }> };
  inputs?: Record<string, unknown>;
  [k: string]: unknown;
}

const isMetric = (v: unknown): v is CentileMetric => typeof v === 'string' && (METRICS as string[]).includes(v);
const isSex = (v: unknown): v is CentileSex => typeof v === 'string' && (SEXES as string[]).includes(v);

export class CentilesSim {
  private constructor(
    readonly files: SimFiles,
    readonly header: CentilesHeader,
    readonly series: CentileSeries[],
  ) {}

  static async open(files: SimFiles): Promise<CentilesSim> {
    const h = await files.json<CentilesHeader>('header.json');
    if (h.format !== 'lifetime-centiles-v0') throw new Error(`sims/${files.dir}: ${String(h.format)}, not lifetime-centiles-v0`);
    const curves = await files.json<{ series?: unknown }>(h.series?.path ?? 'curves.json');
    const list = Array.isArray(curves.series) ? (curves.series as CentileSeries[]) : [];
    if (!list.length) throw new Error(`sims/${files.dir}: no series`);
    const seen = new Set<string>();
    for (const s of list) {
      if (!isMetric(s.metric) || !isSex(s.sex)) throw new Error(`sims/${files.dir}: a series for ${String(s.metric)} / ${String(s.sex)}`);
      const key = `${s.metric}/${s.sex}`;
      if (seen.has(key)) throw new Error(`sims/${files.dir}: ${key} twice`);
      seen.add(key);
      const ages = s.age_post_conception_days;
      if (!Array.isArray(ages) || ages.length < 2) throw new Error(`sims/${files.dir}: ${key} has under two ages`);
      for (let i = 1; i < ages.length; i++) if (!(ages[i]! > ages[i - 1]!)) throw new Error(`sims/${files.dir}: ${key}’s ages don’t increase at ${i}`);
      for (const k of CENTILE_KEYS) {
        const v = s.centiles_ml?.[k];
        if (!Array.isArray(v) || v.length !== ages.length) throw new Error(`sims/${files.dir}: ${key}’s ${k} centile isn’t one value an age`);
      }
      for (let i = 0; i < ages.length; i++) {
        const lo = s.centiles_ml['025'][i]!;
        const md = s.centiles_ml['500'][i]!;
        const hi = s.centiles_ml['975'][i]!;
        if (!(Number.isFinite(lo) && lo > 0 && lo <= md && md <= hi)) throw new Error(`sims/${files.dir}: ${key}’s centiles aren’t finite, positive and ordered at age ${ages[i]}`);
      }
    }
    return new CentilesSim(files, h, list);
  }

  /** The metrics present, in the contract's order. */
  get metrics(): CentileMetric[] {
    return METRICS.filter((m) => this.series.some((s) => s.metric === m));
  }

  /** One metric's series for a sex, or null. */
  of(metric: CentileMetric, sex: CentileSex): CentileSeries | null {
    return this.series.find((s) => s.metric === metric && s.sex === sex) ?? null;
  }

  /** The ages the series span, post-conception days. */
  get ageRange(): [number, number] {
    let lo = Infinity;
    let hi = -Infinity;
    for (const s of this.series) {
      lo = Math.min(lo, s.age_post_conception_days[0]!);
      hi = Math.max(hi, s.age_post_conception_days[s.age_post_conception_days.length - 1]!);
    }
    return [lo, hi];
  }

  /** The centiles' probabilities, the header's or the contract's. */
  get probabilities(): number[] {
    return this.header.series?.probabilities ?? [0.025, 0.5, 0.975];
  }
}
